import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';

import { User } from '../../core/models/user.model';
import { UserService } from '../../core/services/user.service';
import { AuthService } from '../../core/services/auth.service';

import { Meta, Title } from '@angular/platform-browser';
import { DesignService } from '../../core/services/design.service';
import { LoaderService } from '../../core/loader.service';
import { ProfileService } from '../../core/services/profile.service';
import { ClarityService } from '../../core/services/clarity.service';
import { OnboardingReturnService } from '../../core/services/onboarding-return.service';
import { getAccountStatus } from '../../core/util/account-status.util';

declare var clarity: any;
@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
})
export class SigninComponent implements OnInit {
  // user
  user: User = new User();
  navigation: any;
  // flag denoting invalid login i.e wrong email or password.
  invalidLogin: boolean = false;
  loading = false;
  representativeList: Array<String> = [];
  clarity: any;
  constructor(
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private designService: DesignService,
    private title: Title,
    private meta: Meta,
    private loaderService: LoaderService,
    private profileService: ProfileService,
    private route: ActivatedRoute,
    private clarityService: ClarityService,
    private onboardingReturn: OnboardingReturnService
  ) {
    this.designService.setHeaderStyle('auth'); // Set Header Style to auth
    this.navigation = this.router.getCurrentNavigation(); // Get Current URL

    const requestedReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (requestedReturnUrl !== undefined) this.onboardingReturn.save(requestedReturnUrl);
    else this.onboardingReturn.clear();
    const user = this.authService.getUser(); // Get user from session
    if (user) {
      const returnUrl = this.onboardingReturn.peek();
      const roleId = user.roleProtectedObjectList?.[0]?.roleId;
      if (roleId === 1) {
        // If the logged in user is admin
        this.router.navigate(['/category']); // navigate to category
      } else if (roleId === 2) {
        // If the logged in user is not admin
        if (this.onboardingReturn.isEventDestination()) {
          this.router.navigateByUrl(this.onboardingReturn.consume());
          return;
        }
        if (user.verified === false || user.verified === null) {
          // user is not verified.
          if (returnUrl) {
            this.router.navigate(['/signup']);
          } else {
            this.router.navigate(['/portal']); // navigate to portal
          }
        } else if (user.verified === true) {
          if (this.onboardingReturn.isEventDestination()) {
            this.router.navigateByUrl(this.onboardingReturn.consume());
            return;
          }
          // user is verified.
          if (!getAccountStatus(user).hasPage && !getAccountStatus(user).claimPending) {
            this.router.navigate(['/signup']);
          } else {
            if (returnUrl) {
              this.router.navigateByUrl(this.onboardingReturn.consume());
            } else {
              this.router.navigate(['/portal']); // navigate to portal
            }
          }
        }
      }
    }
    this.title.setTitle('CitizeX');
    let description =
      'CitizeX is social networking service based on United states politicians where all levels of government politicians can interact with message known as "Post a Review." Verified politicians can give review and response, but unregistered politicians can only read them.';
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.removeTag('name= "keywords"');
  }
  ngOnInit() {}

  // takes the login credentials as input and passes it to service method.
  // used for logging in to the account
  validateLogin(signinForm: NgForm) {
    if (signinForm.invalid) {
      return;
    }
    this.loading = true;
    this.user.userName = signinForm.form.value.userName.trim().toLowerCase();
    this.user.email = signinForm.form.value.userName.trim().toLowerCase();
    this.user.password = signinForm.form.value.password;

    const requestedReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (requestedReturnUrl !== undefined) this.onboardingReturn.save(requestedReturnUrl);
    else this.onboardingReturn.clear();
    const returnUrl = this.onboardingReturn.peek();
    this.userService
      .login(this.user, signinForm.form.value.remMe === true)
      .subscribe(
        async (success) => {
          /* if (success.success === 1) {
          this.invalidLogin = true;
        } else */
          if (success.success === 0) {
            if (success.data) {
              this.normalizeEmailPreferences(success.data);
            }
            if (
              !this.authService.startSession(
                success,
                signinForm.form.value.remMe === true
              )
            ) {
              this.invalidLogin = true;
              this.loading = false;
              return;
            }
            this.invalidLogin = false;
            this.loading = false;
            await this.clarityService.trackUser({
              userId: success.data.userId,
              email: success.data.email,
              phoneNumber: success.data.phoneNumber,
            });
            if (success.data.address != null) {
              this.getRepresentative(success.data.address);
            }
            if (success.data.roleProtectedObjectList[0].roleId === 1) {
              // If the logged in user is admin
              this.router.navigate(['/category']);
            } else if (success.data.roleProtectedObjectList[0].roleId === 2) {
              // If the logged in user is not admin
              if (this.onboardingReturn.isEventDestination()) {
                this.router.navigateByUrl(this.onboardingReturn.consume());
                return;
              }
              if (
                success.data.verified === false ||
                success.data.verified === null
              ) {
                // user is not verified.
                if (returnUrl) {
                  this.router.navigate(['/signup']);
                } else {
                  this.router.navigate(['/portal']);
                }
              } else if (success.data.verified === true) {
                // user is verified.
                if (!getAccountStatus(success.data).hasPage && !getAccountStatus(success.data).claimPending) {
                  // this.router.navigate(['/profile/search-profile']);
                  this.router.navigate(['/signup']);
                } else {
                  // ✅ Redirect to returnUrl if available
                  if (returnUrl) {
                    this.router.navigateByUrl(this.onboardingReturn.consume());
                  } else {
                    this.router.navigate(['/portal']);
                  }
                }
              }
            }
          } else {
            this.invalidLogin = true;
            this.loading = false;
          }
        },
        (error) => {
          this.invalidLogin = true;
          this.loading = false;
        }
      );
  }

  private normalizeEmailPreferences(userData: any): void {
    if (
      userData.isEmailNotified !== undefined &&
      userData.isEmailNotified !== null
    ) {
      userData.isEmailNotified = Boolean(userData.isEmailNotified);
      userData.isUnsubscribed = !userData.isEmailNotified;
      return;
    }

    if (
      userData.isUnsubscribed !== undefined &&
      userData.isUnsubscribed !== null
    ) {
      userData.isUnsubscribed = Boolean(userData.isUnsubscribed);
      userData.isEmailNotified = !userData.isUnsubscribed;
    }
  }

  // For fetching representative list according to address
  getRepresentative(address: string) {
    address = address.replace(/\n/g, ' '); // Replaces \n with a space
    this.loaderService.disable = true;

    this.profileService.getReprentatives(address).subscribe((res) => {
      if (res.success === 0) {
        res.data.forEach((obj: any) => {
          this.representativeList.push(obj.tagname);
        });
        this.authService.setRepresentativeList(this.representativeList);
      }
    });
  }
}
